/**
 * Cột "Chi tiết" của dòng đơn hàng / báo giá / phiếu sản xuất: chất liệu trước, rồi các công đoạn.
 * VD: "Ivory 300gsm, In Offset, Tráng phủ, Ép nhũ, In lụa UV, Bế nổi, Bế, Dán".
 * Trước đây khi sản phẩm có danh sách công đoạn thì chỉ in công đoạn, mất chất liệu.
 */

type MaterialLike = { name?: string | null; gsm?: string | number | null; code?: string | null; materialId?: string | null } | null | undefined;

const PAPER_HINT = /giấy|ivory|duplex|couche|kraft|bristol|carton|ford|metalize|decal|màng|board/i;

/** Tên vật tư → nhãn gọn: "Giấy Ivory 350 gsm (I350)" → "Ivory 350gsm"; "Giấy D250 Duplex" → "Duplex 250gsm" */
export function materialShortLabel(m: MaterialLike): string {
  const raw = String(m?.name || '').trim();
  if (!raw) return '';
  let name = raw
    .replace(/^giấy\s+/i, '')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+(kt|khổ|kích thước|size)\b.*$/i, '')
    .replace(/\s+\d+\s*[x*×]\s*\d+.*$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  const gsmFromField = m?.gsm != null && String(m.gsm).match(/\d{2,4}/)?.[0];
  const gsmFromName = raw.match(/(\d{2,4})\s*gsm/i)?.[1];
  const gsmFromCode = (raw.match(/\b[A-Z]{1,3}(\d{2,4})\b/)?.[1]) || String(m?.code || m?.materialId || '').match(/^[A-Z]{1,3}(\d{2,4})$/i)?.[1];
  const gsm = gsmFromField || gsmFromName || gsmFromCode;
  const typeWord = name
    .split(' ')
    .find((t) => t && !/^\d/.test(t) && !/^[A-Z]{1,3}\d{2,4}$/i.test(t) && !/gsm/i.test(t));
  if (gsm && typeWord && PAPER_HINT.test(raw)) return `${typeWord} ${gsm}gsm`;
  return name || raw;
}

/** Vật tư chính của dòng: vật tư của sản phẩm, không có thì vật tư giấy đầu tiên trong định mức sản phẩm */
export function primaryMaterialOf(item: any): MaterialLike {
  const p = item?.product;
  if (p?.material?.name) return p.material;
  const list: any[] = (p?.productMaterials || []).map((pm: any) => pm?.material).filter((m: any) => m?.name);
  return list.find((m) => PAPER_HINT.test(m.name)) || list[0] || null;
}

/** Chuỗi "Chi tiết": chất liệu + công đoạn (bỏ trùng, giữ thứ tự) */
export function orderLineDetailText(item: any): string {
  const processNames: string[] = (item?.product?.productProcesses || [])
    .map((pp: any) => pp?.process?.name)
    .filter(Boolean);
  const legacy = String(item?.process || item?.product?.process || '')
    .replace(/\+/g, ', ')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const material = materialShortLabel(primaryMaterialOf(item));
  // Có danh sách công đoạn: chất liệu + công đoạn. Không có: dùng chuỗi quy trình đã lưu (thường đã có chất liệu ở đầu)
  // Chuỗi quy trình cũ có chất liệu ở đầu ("Giấy Ivory 350 gsm (I350), In Offset...") → rút gọn chất liệu cho thống nhất
  const legacyNorm = legacy.length && PAPER_HINT.test(legacy[0]) ? [materialShortLabel({ name: legacy[0] }), ...legacy.slice(1)] : legacy;
  const parts = processNames.length
    ? [material, ...processNames]
    : legacyNorm.length
      ? material && !PAPER_HINT.test(legacy[0]) ? [material, ...legacyNorm] : legacyNorm
      : [material];
  const seen = new Set<string>();
  return parts
    .filter(Boolean)
    .filter((s) => {
      const k = s.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .join(', ');
}
